import React, { forwardRef, useState, useEffect } from 'react';
import { IVideoTreatmentDetail, IVideoTreatment } from 'types/treatment';
import s from './s.module.less';
import dayjs from 'dayjs';
import PrescriptionCard from 'components/PrescriptionCard';
import { formatUTCTime } from 'utils/common';
import { sanitizeRichHtml } from 'utils/sanitize';
import { getProviderNameDisplay } from 'utils/provider';
import ProviderStore from 'store/Provider';
import Logo from 'assets/logo/KlarityLogo.png';
import { providerGetKlarityResourcesUrl } from 'api/provider';
import { Image } from 'antd';

interface Attachment {
    url: string;
    name: string;
    type: string;
}

const PrintContent = forwardRef<HTMLDivElement, { data?: IVideoTreatment; caseData: IVideoTreatmentDetail }>(
    ({ data, caseData }, ref) => {
        const [getUser] = ProviderStore.useStore();
        const [attachments, setAttachments] = useState<Attachment[]>([]);

        useEffect(() => {
            const fetchFileUrl = async () => {
                if (data?.attachments && data?.attachments.length > 0) {
                    try {
                        const allRes = await Promise.all(
                            data?.attachments.map(async (attachment) => {
                                const res = await providerGetKlarityResourcesUrl(attachment.path);
                                return {
                                    url: res.data?.data?.singedUrl,
                                    name: attachment.name || '',
                                    type: attachment.type || '',
                                };
                            }),
                        );
                        setAttachments(allRes);
                    } catch (error) {
                        console.error(error);
                    }
                }
            };
            fetchFileUrl();
        }, [data?.attachments]);

        if (!data) return null;
        const patientName =
            caseData?.patientFirstName && caseData?.patientLastName
                ? `${caseData.patientFirstName} ${caseData.patientLastName}`
                : 'Patient Name';

        const providerName = getProviderNameDisplay(getUser('data'));
        const providerCredentials = getUser('data')?.credential;
        const appointmentDate = formatUTCTime(caseData.startAt, 'dddd, MMMM D, YYYY');
        const appointmentTime = formatUTCTime(caseData.startAt, 'hh:mm A');

        const prescriptions = caseData?.prescriptions;

        return (
            <div className={s.content} ref={ref}>
                <div className={s.printHeader}>
                    <span>Visit Notes</span>
                    <Image src={Logo} alt="logo" width={120} height={40} />
                </div>

                <div className={s.printBody}>
                    <div className={`${s.section} ${s.infoSection}`}>
                        <div className={s.info}>
                            <span className={s.label}>Client:</span> {patientName}
                        </div>
                        <div className={s.info}>
                            <span className={s.label}>Date of birth:</span> {caseData.patientBirthday}
                        </div>
                        <div className={s.info}>
                            <span className={s.label}>Visit Date and time: </span> {appointmentDate} {appointmentTime}
                        </div>
                        <div className={s.info}>
                            <span className={s.label}>Visit Type: </span>
                            {caseData.serviceType}
                        </div>
                        <div className={s.info}>
                            <span className={s.label}>Provider:</span> {providerName}, {providerCredentials}
                        </div>
                    </div>

                    <div className={s.section}>
                        <h3 className={s.sectionTitle}>1. Provider's response</h3>
                        <div
                            className={s.treatmentContent}
                            dangerouslySetInnerHTML={{
                                __html: sanitizeRichHtml(data?.treatmentPlan || caseData.treatmentPlan?.treatmentPlan || ''),
                            }}
                        />
                    </div>

                    {attachments && attachments.length > 0 && (
                        <div className={s.attachments}>
                            {attachments.map((attachment, index) => (
                                <>
                                    {attachment.type.includes('image') && (
                                        <div key={index} className={s.attachment}>
                                            <Image
                                                src={attachment.url}
                                                alt={attachment.name}
                                                width={100}
                                                height={100}
                                            />
                                        </div>
                                    )}
                                    {attachment.type.includes('pdf') && (
                                        <div key={index} className={s.attachment}>
                                            <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                                                {attachment.name}
                                            </a>
                                        </div>
                                    )}
                                </>
                            ))}
                        </div>
                    )}

                    {prescriptions && prescriptions.length > 0 && (
                        <div className={s.section}>
                            <h3 className={s.sectionTitle}>2. Prescription</h3>
                            <div className={s.prescriptionCards}>
                                <div className={s.prescriptionCard}>
                                    {prescriptions.map((prescription, index) => (
                                        <PrescriptionCard key={index} prescription={prescription} isForPrint />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* for style */}
                    <h4 />

                    {/* Provider Signature */}
                    <div className={`${s.section} ${s.infoSection}`}>
                        <div className={s.info}>
                            <span className={s.label}>Signed by provider:</span> {providerName}, {providerCredentials}
                        </div>
                        <div className={s.info}>
                            <span className={s.label}>Signed date and time:</span>{' '}
                            {dayjs().format('dddd, MMMM D, YYYY, hh:mm A')}
                        </div>
                    </div>
                </div>
            </div>
        );
    },
);

export default PrintContent;
